import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Star, 
  TrendingUp, 
  Download,
  RefreshCw,
  Zap
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { apiClient } from '../../lib/api/client';

interface AnalysisResults {
  overall_fitment_score: number;
  parameter_analysis: {
    skills: ParameterAnalysis;
    experience: ParameterAnalysis;
    education: ParameterAnalysis;
    location: ParameterAnalysis;
    salary: ParameterAnalysis;
    cultural_fit: ParameterAnalysis;
  };
  recommendations: string[];
  strengths: string[];
  concerns: string[];
}

interface ParameterAnalysis {
  score: number;
  weight: number;
  explanation: string;
  matched_skills?: string[];
  missing_skills?: string[];
  years_match?: boolean;
  domain_match?: boolean;
  degree_match?: boolean;
  institution_tier?: string;
  location_match?: boolean;
  remote_work?: boolean;
  salary_match?: boolean;
  expectation_gap?: string;
  communication_skills?: string;
  team_work?: string;
}

export const JDCVAnalysisPage: React.FC = () => {
  const { candidateId, requirementId } = useParams<{ candidateId: string; requirementId: string }>();
  const navigate = useNavigate();
  
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (candidateId && requirementId) {
      fetchAnalysisResults();
    }
  }, [candidateId, requirementId]);

  const fetchAnalysisResults = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get candidate data which should include analysis results
      const candidate = await apiClient.getCandidate(candidateId!);
      
      if (!candidate) {
        setError('Candidate not found. Please try again.');
        return;
      }
      
      if (candidate.jd_cv_analysis_data) {
        setAnalysisResults(candidate.jd_cv_analysis_data);
      } else {
        setError('Analysis results not found. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch analysis results:', err);
      setError('Failed to load analysis results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await fetchAnalysisResults();
      toast.success('Analysis results refreshed!');
    } catch (err) {
      toast.error('Failed to refresh analysis results');
    } finally {
      setRetrying(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // TODO: Implement PDF download functionality
      toast.success('PDF download will be available soon!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewEnhancedAnalysis = () => {
    navigate(`/candidates/${candidateId}/enhanced-analysis/${requirementId}`);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading analysis results...</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (error) {
    return (
      <ResponsiveContainer>
        <div className="max-w-2xl mx-auto py-8">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Analysis Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <Button onClick={handleRetry} disabled={retrying}>
                {retrying ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!analysisResults) {
    return (
      <ResponsiveContainer>
        <div className="text-center py-8">
          <p className="text-gray-600">No analysis results available</p>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      <div className="max-w-6xl mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">JD vs CV Analysis</h1>
              <p className="text-gray-600">Comprehensive candidate analysis results</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Button onClick={handleViewEnhancedAnalysis}>
              <Zap className="w-4 h-4 mr-2" />
              Enhanced View
            </Button>
            <Button variant="outline" onClick={handleRetry} disabled={retrying}>
              {retrying ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Refresh
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Overall Score */}
        <Card className="mb-8">
          <div className="text-center py-8">
            <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${getScoreBgColor(analysisResults.overall_fitment_score)} mb-4`}>
              <span className={`text-4xl font-bold ${getScoreColor(analysisResults.overall_fitment_score)}`}>
                {analysisResults.overall_fitment_score}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Overall Fitment Score</h2>
            <p className="text-gray-600">
              {analysisResults.overall_fitment_score >= 80 ? 'Excellent Match' :
               analysisResults.overall_fitment_score >= 60 ? 'Good Match' : 'Needs Improvement'}
            </p>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Parameter Analysis */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Parameter Analysis</h3>
            
            {Object.entries(analysisResults.parameter_analysis).map(([key, analysis]) => (
              <Card key={key}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 capitalize">
                      {key.replace('_', ' ')}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className={`text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                        {analysis.score}
                      </span>
                      <span className="text-sm text-gray-500">
                        (Weight: {Math.round(analysis.weight * 100)}%)
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{analysis.explanation}</p>
                  
                  {/* Additional details based on parameter type */}
                  {key === 'skills' && analysis.matched_skills && (
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-green-600">Matched Skills:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {analysis.matched_skills.map((skill, index) => (
                            <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      {analysis.missing_skills && analysis.missing_skills.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-red-600">Missing Skills:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {analysis.missing_skills.map((skill, index) => (
                              <span key={index} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {key === 'experience' && (
                    <div className="flex space-x-4 text-sm">
                      {analysis.years_match && (
                        <span className="flex items-center text-green-600">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Years Match
                        </span>
                      )}
                      {analysis.domain_match && (
                        <span className="flex items-center text-green-600">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Domain Match
                        </span>
                      )}
                    </div>
                  )}
                  
                  {key === 'education' && (
                    <div className="flex space-x-4 text-sm">
                      {analysis.degree_match && (
                        <span className="flex items-center text-green-600">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Degree Match
                        </span>
                      )}
                      {analysis.institution_tier && (
                        <span className="text-gray-600">
                          Institution Tier: {analysis.institution_tier}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Recommendations and Insights */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Recommendations & Insights</h3>
            
            {/* Strengths */}
            <Card>
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {analysisResults.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* Concerns */}
            <Card>
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <XCircle className="w-5 h-5 mr-2 text-red-600" />
                  Areas for Improvement
                </h4>
                <ul className="space-y-2">
                  {analysisResults.concerns.map((concern, index) => (
                    <li key={index} className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{concern}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* Recommendations */}
            <Card>
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Star className="w-5 h-5 mr-2 text-blue-600" />
                  Recommendations
                </h4>
                <ul className="space-y-2">
                  {analysisResults.recommendations.map((recommendation, index) => (
                    <li key={index} className="flex items-start">
                      <Star className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </ResponsiveContainer>
  );
};
